/* Elevate Tech cinematic homepage engine.
   Scroll-scrubbed hero, band choreography, and the one interactive moment.
   Everything animated here is transform and opacity only. */

(function () {
  'use strict';

  // The five static-hero gates. These strings match elevate-cinematic.css exactly.
  var GATES = [
    '(max-width: 720px)',
    '(orientation: portrait) and (max-width: 1024px)',
    '(orientation: portrait) and (pointer: coarse)',
    '(orientation: landscape) and (pointer: coarse) and (max-height: 560px)',
    '(prefers-reduced-motion: reduce)'
  ];

  var clamp = function (v, lo, hi) { return Math.min(hi, Math.max(lo, v)); };
  var smoothstep = function (p, e0, e1) {
    var t = clamp((p - e0) / (e1 - e0), 0, 1);
    return t * t * (3 - 2 * t);
  };

  function initHero(root) {
    if (!root || root.dataset.evInit === '1') return;
    root.dataset.evInit = '1';

    var hero = root.querySelector('.ev-hero');
    if (!hero) return;
    var stage = root.querySelector('.ev-stage');
    var video = root.querySelector('.ev-video');
    var posterLayer = root.querySelector('.ev-poster');
    var cue = root.querySelector('.ev-cue');
    var ring = root.querySelector('.ev-ring circle');
    var bandEls = [].slice.call(root.querySelectorAll('.ev-band'));

    var VIDEO_URL = root.dataset.video || '';
    var POSTER_URL = root.dataset.poster || '';
    var VIDEO_BYTES = Number(root.dataset.videoBytes) || 9000000;

    // Band ranges come from the design package's band map.
    var bands = bandEls.map(function (el, i) {
      var a = parseFloat(el.dataset.from);
      var b = parseFloat(el.dataset.to);
      return {
        el: el,
        a: isNaN(a) ? i * 0.25 : a,
        b: isNaN(b) ? i * 0.25 + 0.22 : b,
        first: i === 0,
        last: i === bandEls.length - 1,
        mode: el.dataset.mode || 'rise',
        op: -1,
        k: -1,
        chars: [].slice.call(el.querySelectorAll('.ev-ch'))
      };
    });

    var target = 0, shown = 0, rafId = null, lastTick = 0;
    var seekBusy = false, pendingTime = null;
    var heroOnScreen = true, scrubOn = false, started = false;
    var loadK = 0, loadStart = 0;

    /* Horizon does not scroll the window. It puts overflow:hidden on html and
       body and scrolls an inner wrapper instead, and scroll events do not bubble
       from an element to window. A window listener therefore never fires and the
       hero sits frozen on the first frame. Find whatever actually scrolls and
       listen to that; window is the fallback for a normally-scrolling page. */
    function findScroller(el) {
      var n = el.parentElement;
      while (n && n !== document.body && n !== document.documentElement) {
        var cs = getComputedStyle(n);
        if (/(auto|scroll|overlay)/.test(cs.overflowY) && n.scrollHeight > n.clientHeight + 1) return n;
        n = n.parentElement;
      }
      return window;
    }
    var scroller = null;

    function heroProgress() {
      if (!hero) return 0;
      var range = hero.offsetHeight - window.innerHeight;
      if (range <= 0) return 0;
      // Viewport-relative, so this stays correct whichever element is scrolling.
      var top = hero.getBoundingClientRect().top;
      return clamp(-top / range, 0, 1);
    }

    /* ---- gated seeks, deadlock safe ---- */
    function requestSeek(t) {
      if (!video || !video.duration) return;
      if (seekBusy) { pendingTime = t; return; }
      seekBusy = true;
      try { video.currentTime = t; } catch (e) { seekBusy = false; }
    }
    if (video) {
      video.addEventListener('seeked', function () {
        seekBusy = false;
        if (pendingTime !== null) {
          var t = pendingTime;
          pendingTime = null;
          requestSeek(t);
        }
      });
      video.addEventListener('error', function () {
        seekBusy = false; pendingTime = null; failVideo();
      });
    }

    /* ---- band + character choreography, delta gated ---- */
    function applyChars(band, k) {
      var n = band.chars.length;
      if (!n) return;
      for (var i = 0; i < n; i++) {
        var ch = band.chars[i];
        var seed = Number(ch.dataset.seed) || 0;
        var stagger = n > 1 ? i / (n - 1) : 0;
        var local = clamp((k - stagger * 0.35) / 0.65, 0, 1);
        var eased = local * local * (3 - 2 * local);
        var y = 0, o = eased;
        if (band.mode === 'resolve') {          // band 1: chaos settling, echoing the particles
          y = (1 - eased) * (seed * 26 - 13);
        } else if (band.mode === 'rise') {      // band 2: rises with the descent
          y = (1 - eased) * 30;
        } else if (band.mode === 'snap') {      // band 3: lands on the lens-blur beat
          o = local < 0.55 ? 0 : smoothstep(local, 0.55, 0.85);
          y = (1 - eased) * 10;
        } else if (band.mode === 'settle') {    // band 4: tightens to rest
          y = 0;
          ch.style.setProperty('--chX', ((1 - eased) * (seed * 18 - 9)).toFixed(2) + 'px');
        }
        ch.style.setProperty('--chO', o.toFixed(3));
        ch.style.setProperty('--chY', y.toFixed(2) + 'px');
      }
    }

    function updateCaptions(p) {
      for (var i = 0; i < bands.length; i++) {
        var band = bands[i];
        var f = Math.min(0.02, (band.b - band.a) / 3);
        var inRamp = band.first ? 1 : smoothstep(p, band.a, band.a + f);
        var outRamp = band.last ? 1 : (1 - smoothstep(p, band.b - f, band.b));
        var op = inRamp * outRamp;

        var ramp = parseFloat(band.el.dataset.ramp) || Math.min(0.025, (band.b - band.a) * 0.35);
        var k = clamp((p - band.a) / ramp, 0, 1);
        if (band.first) k = Math.max(k, loadK);

        if (Math.abs(op - band.op) > 0.004) {         // delta gated
          band.op = op;
          band.el.style.opacity = op.toFixed(3);
        }
        if (Math.abs(k - band.k) > 0.008) {
          band.k = k;
          band.el.style.setProperty('--k', k.toFixed(3));
          applyChars(band, k);
        }
      }
    }

    /* ---- the drive loop ----
       This reads the hero's own position every frame rather than trusting a
       scroll event to tell it something moved. No single listener sees every
       case: Horizon scrolls an inner wrapper, the theme editor scrolls a frame
       around the page, and element scroll events do not bubble to window. A
       bounding rect always tells the truth about where the hero actually is.

       The cost is one rAF while the hero is on screen, which is exactly the
       stretch where scrubbing happens. It stops the moment the hero leaves, and
       every DOM write inside it is still delta-gated, so a stationary page costs
       little more than the callback itself. */
    function tick(now) {
      var dt = Math.min(100, now - (lastTick || now));
      lastTick = now;
      var k = 0.16;

      target = heroProgress();
      shown += (target - shown) * (1 - Math.pow(1 - k, dt / 16.667));
      if (Math.abs(target - shown) < 0.0005) shown = target;

      if (loadK < 1 && loadStart) {                   // band one's one-time load ramp
        loadK = clamp((now - loadStart) / 900, 0, 1);
      }

      if (video && video.duration) requestSeek(shown * video.duration);
      updateCaptions(shown);

      if (scrubOn && heroOnScreen) {
        rafId = requestAnimationFrame(tick);
      } else {
        rafId = null;
        lastTick = 0;
      }
    }

    function startLoop() {
      if (rafId === null && scrubOn && heroOnScreen) {
        lastTick = 0;
        rafId = requestAnimationFrame(tick);
      }
    }

    function onScroll() { startLoop(); }

    /* ---- the streamed Blob with an honest ring ---- */
    function setRing(frac) {
      if (ring) ring.style.strokeDashoffset = String(Math.round(126 * (1 - frac)));
    }

    function failVideo() {
      if (!stage) return;
      stage.classList.add('is-video-failed');
      if (cue) cue.innerHTML = '<svg class="ev-chev" viewBox="0 0 26 44" aria-hidden="true">' +
        '<path d="M5 16l8 8 8-8" fill="none" stroke="currentColor" stroke-width="2" ' +
        'stroke-linecap="round" stroke-linejoin="round"/></svg>';
    }

    function loadHeroBlob() {
      var ctrl = new AbortController();
      var watchdog = setTimeout(function () { ctrl.abort(); }, 20000);
      return fetch(VIDEO_URL, { priority: 'low', signal: ctrl.signal }).then(function (res) {
        if (!res.ok || !res.body) throw new Error('bad response');
        var total = Number(res.headers.get('Content-Length')) || VIDEO_BYTES;
        var reader = res.body.getReader();
        var chunks = [], got = 0, lastRing = 0;
        function pump() {
          return reader.read().then(function (r) {
            if (r.done) return;
            clearTimeout(watchdog);
            watchdog = setTimeout(function () { ctrl.abort(); }, 20000);
            chunks.push(r.value);
            got += r.value.length;
            var frac = Math.min(1, got / total);
            var now = performance.now();
            if (now - lastRing > 100 || frac === 1) { lastRing = now; setRing(frac); }
            return pump();
          });
        }
        return pump().then(function () {
          clearTimeout(watchdog);
          setRing(1);
          video.src = URL.createObjectURL(new Blob(chunks));
          video.load();
          video.addEventListener('canplay', function () {
            requestSeek(heroProgress() * video.duration);
            stage.classList.add('is-video-ready');
            if (cue) cue.style.opacity = '0';
          }, { once: true });
        });
      });
    }

    function initHeroOnce() {
      if (started) return;
      started = true;
      loadStart = performance.now();
      if (POSTER_URL && posterLayer) {
        posterLayer.style.backgroundImage = "url('" + POSTER_URL + "')";
      }
      if (!VIDEO_URL) { failVideo(); return; }   // complete without the video, by design
      var kick = function () { loadHeroBlob().catch(failVideo); };
      if (POSTER_URL) {
        var img = new Image();
        var fired = false;
        var once = function () { if (!fired) { fired = true; kick(); } };
        img.onload = once; img.onerror = once;
        img.src = POSTER_URL;
        setTimeout(once, 4000);
      } else { kick(); }
    }

    /* ---- the gate, decided live ---- */
    function enableScrub() {
      if (scrubOn) return;
      scrubOn = true;
      initHeroOnce();
      scroller = findScroller(hero);
      if (scroller !== window) scroller.addEventListener('scroll', onScroll, { passive: true });
      // Capture phase, because an element's scroll event does not bubble to
      // window but does travel down the capture path. This catches a scroll from
      // any container without having to guess which one it is.
      window.addEventListener('scroll', onScroll, { passive: true, capture: true });
      window.addEventListener('wheel', onScroll, { passive: true });
      window.addEventListener('touchmove', onScroll, { passive: true });
      bands.forEach(function (b) { b.op = -1; b.k = -1; });
      root.classList.remove('ev-pinned');
      updateCaptions(heroProgress());
      startLoop();
    }
    function disableScrub() {
      if (!scrubOn) return;
      scrubOn = false;
      if (scroller && scroller !== window) scroller.removeEventListener('scroll', onScroll);
      scroller = null;
      window.removeEventListener('scroll', onScroll, { capture: true });
      window.removeEventListener('wheel', onScroll);
      window.removeEventListener('touchmove', onScroll);
      if (rafId !== null) { cancelAnimationFrame(rafId); rafId = null; }
    }
    function applyHeroMode() {
      var gated = GATES.some(function (q) { return matchMedia(q).matches; });
      if (gated) disableScrub(); else enableScrub();
    }
    var MQLS = GATES.map(function (q) { return matchMedia(q); });
    MQLS.forEach(function (m) {
      if (m.addEventListener) m.addEventListener('change', applyHeroMode);
      else if (m.addListener) m.addListener(applyHeroMode);
    });

    if (hero && 'IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        heroOnScreen = entries[0].isIntersecting;
        if (heroOnScreen && scrubOn) startLoop();
      }, { rootMargin: '10% 0px' }).observe(hero);
    }
    window.addEventListener('resize', function () { if (scrubOn) startLoop(); }, { passive: true });

    applyHeroMode();

    /* Read-only diagnostic. Turns "the hero does not move" into one object
       instead of a guessing game. Costs nothing and touches nothing. */
    window.__evHero = {
      get state() {
        return {
          scrubArmed: scrubOn,
          loopRunning: rafId !== null,
          heroOnScreen: heroOnScreen,
          scroller: scroller === window ? 'window' : (scroller && scroller.className) || 'none',
          heroHeightPx: hero.offsetHeight,
          progress: +heroProgress().toFixed(3),
          shown: +shown.toFixed(3),
          videoSrcSet: !!(video && video.src),
          videoDuration: (video && video.duration) || null,
          videoTime: (video && video.currentTime) || 0,
          stageClasses: stage ? stage.className : null,
          gatesMatched: GATES.filter(function (q) { return matchMedia(q).matches; })
        };
      }
    };

    /* ---- reduced motion, honored live in both directions ---- */
    var rm = matchMedia('(prefers-reduced-motion: reduce)');
    var onRM = function (e) {
      if (e.matches) { root.classList.add('ev-pinned'); pinToFinalStates(root); disableScrub(); }
      else { applyHeroMode(); }
    };
    if (rm.addEventListener) rm.addEventListener('change', onRM);
    else if (rm.addListener) rm.addListener(onRM);
  }

  function pinToFinalStates(root) {
    root.querySelectorAll('.ev-wave').forEach(function (w) { w.classList.add('in'); });
    root.querySelectorAll('.ev-sec, .ev-prod').forEach(function (s) { s.classList.add('ev-in', 'ev-done'); });
    var slider = root.querySelector('.ev-slider');
    if (slider) { slider.value = 100; slider.dispatchEvent(new Event('change')); }
  }

  /* ---- split text into characters for the band choreography ---- */
  function splitChars(root) {
    root.querySelectorAll('[data-split]').forEach(function (el) {
      if (el.dataset.splitDone === '1') return;
      el.dataset.splitDone = '1';
      var text = el.textContent;
      el.textContent = '';
      for (var i = 0; i < text.length; i++) {
        var s = document.createElement('span');
        s.className = 'ev-ch';
        s.textContent = text[i];
        s.dataset.seed = (Math.sin(i * 12.9898) * 43758.5453 % 1 + 1) % 1;
        el.appendChild(s);
      }
    });
  }

  /* ---- entrances, with the stagger properly retired ----
     Everything that should arrive rather than appear. Tagging is done in JS so
     the stagger index is per element and per group, instead of a hand written
     nth-child list. Product blocks are tagged before sections so their children
     stagger within the product rather than from the section's running count. */
  var REVEAL_SEL = [
    '.ev-up',
    '.ev-prod-media', '.ev-prod h3', '.ev-prod p', '.ev-swatches', '.ev-price', '.ev-prod-cta',
    '.ev-kicker', '.ev-h2', '.ev-lede',
    '.ev-anc-row', '.ev-anc-rule', '.ev-anc-note',
    '.ev-manifest-head', '.ev-manifest-row',
    '.ev-card',
    '.ev-noise', '.ev-spec', '.ev-faq details', '.ev-review-row',
    '.ev-signup-head', '.ev-signup-form', '.ev-close-policies'
  ].join(',');

  function prepareReveals(root) {
    // Inner groups first so their children stagger within the group rather than
    // from the enclosing section's running count.
    var groups = [].slice.call(root.querySelectorAll('.ev-prod'))
      .concat([].slice.call(root.querySelectorAll('.ev-manifest')))
      .concat([].slice.call(root.querySelectorAll('.ev-signup')))
      .concat([].slice.call(root.querySelectorAll('.ev-sec')));
    groups.forEach(function (group) {
      var i = 0;
      [].slice.call(group.querySelectorAll(REVEAL_SEL)).forEach(function (el) {
        if (el.dataset.evReveal) return;          // already claimed by an inner group
        el.dataset.evReveal = '1';
        el.classList.add('ev-rise');
        if (el.classList.contains('ev-prod-media')) el.classList.add('ev-rise--media');
        el.style.setProperty('--i', i);
        i++;
      });
      group.dataset.evCount = i;
    });
  }

  function initEntrances(root) {
    prepareReveals(root);

    if (!('IntersectionObserver' in window)) {
      root.querySelectorAll('.ev-sec, .ev-prod, .ev-manifest, .ev-wave').forEach(function (s) {
        s.classList.add('ev-in', 'in', 'ev-done');
      });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        var el = e.target;
        el.classList.add('ev-in', 'in');
        // Wait for the slowest child: the longest transition plus the last
        // element's stagger. Retiring early would strand a delay on it.
        var count = Number(el.dataset.evCount) || 0;
        setTimeout(function () { el.classList.add('ev-done'); }, 1400 + count * 65);
        io.unobserve(el);
      });
      // A group entering starts its own reveal; nothing else needs waking.
    }, { rootMargin: '0px 0px -10% 0px', threshold: 0.1 });
    root.querySelectorAll('.ev-sec, .ev-prod, .ev-manifest, .ev-wave').forEach(function (s) { io.observe(s); });
  }

  /* ---- the signature waveform: jagged at the top, calm by the footer ---- */
  function drawWaves(root) {
    var waves = [].slice.call(root.querySelectorAll('.ev-wave'));
    waves.forEach(function (svg, idx) {
      var path = svg.querySelector('path');
      if (!path) return;
      var calm = waves.length > 1 ? idx / (waves.length - 1) : 1;  // 0 jagged, 1 flat
      var W = 1200, H = 64, mid = H / 2, pts = [];
      for (var x = 0; x <= W; x += 10) {
        var t = x / W;
        var noise = Math.sin(x * 0.13) * 13 + Math.sin(x * 0.41) * 8 + Math.sin(x * 0.07) * 6;
        var y = mid + noise * (1 - calm) * (0.35 + 0.65 * Math.sin(t * Math.PI));
        pts.push((x === 0 ? 'M' : 'L') + x + ' ' + y.toFixed(1));
      }
      path.setAttribute('d', pts.join(' '));
      svg.setAttribute('viewBox', '0 0 ' + W + ' ' + H);
      var len = path.getTotalLength();
      svg.style.setProperty('--len', Math.ceil(len));
    });
  }

  /* ---- featured cards: keep the shown price honest to the chosen variant ----
     This product's colourways are not all the same price, so a single "From"
     figure would go stale the moment someone picks a different one. The price
     for each option is carried on the option itself by Liquid, so this only has
     to copy it across. */
  function initCards(root) {
    root.querySelectorAll('[data-card-variant]').forEach(function (sel) {
      if (sel.dataset.evInit === '1') return;
      sel.dataset.evInit = '1';
      var card = sel.closest('.ev-card');
      var out = card && card.querySelector('[data-card-price]');
      if (!out) return;
      var sync = function () {
        var opt = sel.options[sel.selectedIndex];
        if (opt && opt.dataset.price) out.textContent = opt.dataset.price;
      };
      sel.addEventListener('change', sync);
      sync();
    });
  }

  /* ---- the ANC equation: noise, its mirror, and the two added together ----
     The third path is computed as noise + mirror rather than drawn flat, so the
     diagram cannot drift out of agreement with the claim it is making. */
  function initAnc(root) {
    var paths = {};
    ['noise', 'mirror', 'sum'].forEach(function (k) {
      paths[k] = root.querySelector('[data-anc="' + k + '"]');
    });
    if (!paths.noise || !paths.mirror || !paths.sum) return;

    var W = 1000, H = 90, mid = H / 2;
    function noiseAt(x) {
      var t = x / W;
      var envelope = Math.sin(t * Math.PI);            // fade in and out at the ends
      return (Math.sin(x * 0.031) * 16 +
              Math.sin(x * 0.077) * 9 +
              Math.sin(x * 0.013) * 11) * envelope;
    }

    var d = { noise: [], mirror: [], sum: [] };
    for (var x = 0; x <= W; x += 5) {
      var n = noiseAt(x), m = -n;
      d.noise.push((x === 0 ? 'M' : 'L') + x + ' ' + (mid + n).toFixed(2));
      d.mirror.push((x === 0 ? 'M' : 'L') + x + ' ' + (mid + m).toFixed(2));
      d.sum.push((x === 0 ? 'M' : 'L') + x + ' ' + (mid + n + m).toFixed(2));
    }
    Object.keys(paths).forEach(function (k) {
      paths[k].setAttribute('d', d[k].join(' '));
      var len = paths[k].getTotalLength();
      paths[k].parentNode.style.setProperty('--len', Math.ceil(len));
    });
  }

  /* ---- the one interactive moment: strip the noise off the signal yourself ---- */
  function initNoise(root) {
    var wrap = root.querySelector('.ev-noise');
    if (!wrap || wrap.dataset.evInit === '1') return;
    wrap.dataset.evInit = '1';

    var slider = wrap.querySelector('.ev-slider');
    var stage = wrap.querySelector('.ev-noise-stage');
    var dirty = wrap.querySelector('.ev-noise-dirty');
    var clean = wrap.querySelector('.ev-noise-clean');
    var readout = wrap.querySelector('[data-noise-read]');
    if (!slider || !stage) return;

    var W = 1000, H = 150, mid = H / 2;
    function build(amount) {
      var d = [], c = [];
      for (var x = 0; x <= W; x += 6) {
        var env = Math.sin((x / W) * Math.PI * 2.4) * 26 + Math.sin((x / W) * Math.PI * 7.1) * 12;
        var hiss = (Math.sin(x * 1.7) + Math.sin(x * 3.9) + Math.sin(x * 0.9)) * 11 * amount;
        d.push((x === 0 ? 'M' : 'L') + x + ' ' + (mid + env + hiss).toFixed(1));
        c.push((x === 0 ? 'M' : 'L') + x + ' ' + (mid + env).toFixed(1));
      }
      if (dirty) dirty.setAttribute('d', d.join(' '));
      if (clean) clean.setAttribute('d', c.join(' '));
    }
    build(1);

    var lastRead = '', lastReadAt = 0;
    function render(force) {
      var v = Number(slider.value) / 100;
      stage.style.setProperty('--clean', v.toFixed(3));
      build(1 - v);
      var now = performance.now();
      var text = Math.round(v * 100) + '% removed';
      // Throttled to ~10Hz and written only on change, but a forced write always
      // lands. Without that the last value of a drag can be thrown away by the
      // throttle and the label sits on a number the slider is no longer showing.
      if (readout && text !== lastRead && (force || now - lastReadAt > 100)) {
        lastRead = text; lastReadAt = now;
        readout.textContent = text;
      }
      wrap.classList.toggle('is-clean', v > 0.92);
    }
    slider.addEventListener('input', function () { render(false); });
    slider.addEventListener('change', function () { render(true); });

    // Reduced motion opens on the resolved state instead of asking for a drag.
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) slider.value = 100;
    render(true);
  }

  function boot(scope) {
    var roots = (scope || document).querySelectorAll('.ev-root');
    roots.forEach(function (root) {
      splitChars(root);
      drawWaves(root);
      initAnc(root);
      initCards(root);
      initEntrances(root);
      initNoise(root);
      initHero(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(document); });
  } else { boot(document); }

  // Shopify theme editor re-renders sections; re-run for the new markup.
  document.addEventListener('shopify:section:load', function (e) { boot(e.target); });

  // Pause every loop on a hidden tab. animation-play-state does not inherit,
  // so this has to reach pseudo-elements explicitly via the stylesheet rule.
  document.addEventListener('visibilitychange', function () {
    document.body.classList.toggle('ev-paused', document.hidden);
  });
})();
